import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { AnalyticProvider, useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { DateService } from '@application/time/date.service';
import { useTitle } from '@application/use_title';
import {
	BreakPoints,
	Divider,
	Flex,
	Icon,
	IconDictionary,
	NotificationAlert,
	Spacer,
	Text,
	TextLink,
	useBreakpoint,
	VisuallyHidden,
} from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Async } from '@component/async.component';
import { IframeSheet } from '@component/iframe_sheet.component';
import { Logo } from '@component/logo.component';
import { Shimmer } from '@component/new_shimmer.component';
import { Title } from '@component/title.component';
import { useVonageEncryptionWarning, VonageEncryptionWarning } from '@component/vonage_encryption_warning/mod';
import { Auth } from '@feature/auth/auth.component';
import { useResolve, useResolveController, useService, withNewContainer } from '@visiba/instantiation';
import { ReactNode, useEffect } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { IllustrativeDefault } from './_illustrative.component';
import { FlowError } from './flow_error.component';
import { FlowData, ViewGuestController } from './view_guest.controller';

const StyledContainer = styled.div`
	width: 100%;
	height: 100%;
	margin: ${tokens.spacing.x2} 0 ${tokens.spacing.x1};

	${tokens.breakpointQuery.large} {
		padding-top: 240px;
		max-width: 500px;
		max-height: 700px;
	}
`;

const StyledHeader = styled.div`
	display: none;

	${tokens.breakpointQuery.large} {
		display: block;
		position: fixed;
		top: ${tokens.spacing.x3};
		left: ${tokens.spacing.x3};
		z-index: 1;
	}
`;

const SideAreaContent = styled.div`
	width: 630px;
`;

const SideArea = styled.div`
	position: fixed;
	right: 0;
	top: 0;
	display: flex;
	width: 630px;
	min-height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-shrink: 0;
	padding: 0px 65px;
	background-color: ${tokens.color.bg.surfaceBrand};
`;

function OutletIdp(props: React.PropsWithChildren): JSX.Element {
	const largeBreakpoint = useBreakpoint(BreakPoints.Large);

	return (
		<Flex
			grow={1}
			direction='row'
			style={{
				height: '100%',
				minHeight: '100%',
			}}
		>
			<Flex
				grow={1}
				breakpointLargeAndUp={{
					align: 'center',
				}}
			>
				<StyledContainer>
					<StyledHeader>
						<Logo />
					</StyledHeader>

					{props.children}
				</StyledContainer>
			</Flex>

			{largeBreakpoint ? (
				<SideAreaContent>
					<SideArea>
						<IllustrativeDefault />
					</SideArea>
				</SideAreaContent>
			) : null}
		</Flex>
	);
}

export const ViewGuest = withNewContainer(function ViewGuest(): JSX.Element {
	const analyticService = useService(AnalyticService);

	useTitle(withTranslation('View.Guest.PageTitle'));

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: 'guest' });
		},
		[analyticService],
	);

	return (
		<AnalyticProvider category={AnalyticCategories.Guest}>
			<ViewGuestContent />
		</AnalyticProvider>
	);
});

function ViewGuestContent() {
	const { ticket } = useParams();
	const controller = useResolveController(ViewGuestController, ticket ?? 'no-ticket');
	const { hasEncryptionSupport } = useVonageEncryptionWarning();
	const analyticCtx = useAnalyticContext();

	return (
		<OutletIdp>
			<Flex
				grow='1'
				gap={tokens.spacing.x2}
				style={{
					margin: `0 ${tokens.spacing.x2}`,
					height: '100%',
				}}
				data-testid='sign_in'
			>
				<Async derive={controller.flowData} fallback={<Skeleton />} catch={<FlowError />}>
					{(data) => {
						const start = DateService.newDate(data.calendarInfo.AppointmentStart);
						const end = DateService.newDate(data.calendarInfo.AppointmentEnd);

						return (
							<>
								<Flex gap={tokens.spacing.x4}>
									<Title title={withTranslation('View.Guest.Title')} />

									<Flex gap={tokens.spacing.x1_5}>
										<Text.h2 variant='labelSemiBold'>{withTranslation('View.Guest.Details.Title')}</Text.h2>
										<ListItem icon='calendar'>
											<VisuallyHidden>{withTranslation('View.Guest.Details.Date')}</VisuallyHidden>{' '}
											{DateService.newDate(data.calendarInfo.AppointmentStart).format('D MMMM')}
										</ListItem>
										<ListItem icon='clock'>
											<VisuallyHidden>{withTranslation('View.Guest.Details.Time')}</VisuallyHidden>
											{start.format('HH:mm')}-{end.format('HH:mm')} ({end.diff(start, 'minutes')})
										</ListItem>
										<ListItem icon='house'>
											<VisuallyHidden>{withTranslation('View.Guest.Details.Reception')}</VisuallyHidden>
											{data.receptionName}
										</ListItem>
									</Flex>

									<Divider />
									{data.calendarInfo.VonageEncryptionRequired &&
									!hasEncryptionSupport &&
									['waitingForOpen', 'available'].includes(controller.availability.value) ? (
										<VonageEncryptionWarning />
									) : (
										<>
											{(() => {
												const availability = controller.availability.value;
												switch (availability) {
													case 'waitingForOpen':
														return <StatusWaitingForOpeningHour />;
													case 'cancelled':
														return <StatusCancelled />;
													case 'closed':
														return <StatusClosed />;
													case 'completed':
														return <StatusCompleted />;
													case 'available':
														return <StatusAvailable data={data} controller={controller} ticket={ticket ?? '-'} />;
													default: {
														const _exhaustiveCheck: never = availability;

														analyticCtx.trackEvent('Unknown availability state: ' + availability);

														return null;
													}
												}
											})()}

											<Flex
												style={{
													color: tokens.color.content.default,
												}}
											>
												<Text.p variant='bodySmallRegular'>
													<Text variant='bodySmallRegular'>{withTranslation('View.Guest.Terms.1')}</Text>{' '}
													<IframeSheet
														src={controller.policyDocuments.value?.termsOfService ?? ''}
														trigger={
															<TextLink as='button' size='small' onClick={() => analyticCtx.trackEvent('Clicked ToS')}>
																{withTranslation('Common.ToS')}
															</TextLink>
														}
													/>
													. <Text variant='bodySmallRegular'>{withTranslation('View.Guest.Terms.2')}</Text>{' '}
													<IframeSheet
														src={controller.policyDocuments.value?.privacyPolicy ?? ''}
														trigger={
															<TextLink as='button' size='small' onClick={() => analyticCtx.trackEvent('Clicked PP')}>
																{withTranslation('Common.PP')}
															</TextLink>
														}
													/>
													.
												</Text.p>
											</Flex>
										</>
									)}
								</Flex>

								<Spacer vertical={tokens.spacing.x9} />
							</>
						);
					}}
				</Async>
			</Flex>
		</OutletIdp>
	);
}

function StatusWaitingForOpeningHour() {
	return <NotificationAlert variant='info'>{withTranslation('View.Guest.Notification.WaitingForOpen')}</NotificationAlert>;
}

function StatusCancelled() {
	return <NotificationAlert variant='info'>{withTranslation('View.Guest.Notification.Cancelled')}</NotificationAlert>;
}

function StatusClosed() {
	return <NotificationAlert variant='info'>{withTranslation('View.Guest.Notification.Closed')}</NotificationAlert>;
}

function StatusCompleted() {
	return <NotificationAlert variant='info'>{withTranslation('View.Guest.Notification.Completed')}</NotificationAlert>;
}

function StatusAvailable({ data, controller, ticket }: { data: FlowData; controller: ViewGuestController; ticket: string }) {
	const analyticCtx = useAnalyticContext();

	function handleMethodClick(remoteScheme: string, callticket: string) {
		analyticCtx.trackEvent('Clicked authentication scheme: ' + remoteScheme.toString());
		controller.selectMethod(remoteScheme, callticket);
	}

	if (data.accessInfo.AuthenticationMethod == null) {
		analyticCtx.trackEvent('No authentication schemes found');
	}

	return (
		<>
			<Flex gap={tokens.spacing.x1}>
				<Text.h2 variant='headingXSmall'>{withTranslation('View.Guest.SignIn.Title')}</Text.h2>
				<Text.p variant='bodyRegular'>{withTranslation('View.Guest.SignIn.Description')}</Text.p>
			</Flex>

			<Flex gap={tokens.spacing.x1}>
				<Text.h2 variant='labelSemiBold'>{withTranslation('View.Guest.SignIn.Choices')}</Text.h2>

				{data.accessInfo.AuthenticationMethod ? (
					<Auth.MethodLink
						scheme={data.accessInfo.AuthenticationMethod.AuthenticationMethodId}
						title={data.accessInfo.AuthenticationMethod.Name ?? data.accessInfo.AuthenticationMethod.AuthenticationMethodId}
						description={undefined /* TODO: Add description? */}
						onClick={() => handleMethodClick(data.accessInfo.AuthenticationMethod?.AuthenticationMethodId ?? '-', ticket)}
					/>
				) : (
					<NotificationAlert variant='danger'>No authentication methods is available</NotificationAlert>
				)}
			</Flex>
		</>
	);
}

interface ListItemProps {
	children: ReactNode;
	icon: keyof IconDictionary;
}

function ListItem(props: ListItemProps) {
	return (
		<Flex
			gap={tokens.spacing.x1}
			direction='row'
			align='center'
			style={{
				color: tokens.color.content.default,
			}}
		>
			<Icon type={props.icon} />
			<Text.p variant='labelRegular'>{props.children}</Text.p>
		</Flex>
	);
}

function Skeleton() {
	return (
		<Flex gap={tokens.spacing.x3} grow='1'>
			<Shimmer height={40} width='50%' />

			<Flex gap={tokens.spacing.x0_5}>
				<Shimmer height={15} width='30%' />
				<Shimmer height={15} width='80%' />
				<Shimmer height={15} width='60%' />
				<Shimmer height={15} width='70%' />
			</Flex>
		</Flex>
	);
}
